import type { ToolCallSummary } from './tool-call-summary.js'

/**
 * Vercel UI Message Stream tool-call lifecycle. We don't import the SDK type
 * directly because (a) it's a discriminated-union containing every chunk shape
 * and (b) we only need a tiny structural subset here.
 */
interface ToolInputStart {
  type: 'tool-input-start'
  toolCallId: string
  toolName: string
}
interface ToolInputDelta {
  type: 'tool-input-delta'
  toolCallId: string
  inputTextDelta: string
}
interface ToolInputAvailable {
  type: 'tool-input-available'
  toolCallId: string
  toolName: string
  input: unknown
}
interface ToolOutputAvailable {
  type: 'tool-output-available'
  toolCallId: string
  output: unknown
}
interface ToolOutputError {
  type: 'tool-output-error'
  toolCallId: string
  errorText?: string
}

type ToolPart = ToolInputStart | ToolInputDelta | ToolInputAvailable | ToolOutputAvailable | ToolOutputError

const isObject = (x: unknown): x is Record<string, unknown> => typeof x === 'object' && x !== null

const isToolPart = (part: unknown): part is ToolPart => {
  if (!isObject(part)) {
    return false
  }
  const type = part.type
  const toolCallId = part.toolCallId
  if (typeof toolCallId !== 'string' || toolCallId.length === 0) {
    return false
  }
  return (
    type === 'tool-input-start' ||
    type === 'tool-input-delta' ||
    type === 'tool-input-available' ||
    type === 'tool-output-available' ||
    type === 'tool-output-error'
  )
}

/**
 * Project an ordered array of Vercel UI Message Stream chunks into a stable
 * {@link ToolCallSummary} list. The reducer:
 *  - registers a new call on first sight of a `toolCallId` (preserving order)
 *  - accumulates `inputTextDelta` fragments into `argsText`
 *  - flips state to `called` on `tool-output-available`
 *  - flips state to `errored` on `tool-output-error`
 *
 * Pure — no I/O, no allocation beyond the result. Safe to call on every
 * cache update; consumers can `useMemo`-free per React Compiler.
 */
export const partsToToolCalls = (parts: readonly unknown[]): ToolCallSummary[] => {
  const order: string[] = []
  const byId = new Map<string, ToolCallSummary>()

  const ensure = (id: string, name: string): ToolCallSummary => {
    const existing = byId.get(id)
    if (existing) {
      return existing
    }
    const created: ToolCallSummary = { id, name, state: 'streaming' }
    byId.set(id, created)
    order.push(id)
    return created
  }

  for (const part of parts) {
    if (!isToolPart(part)) {
      continue
    }
    if (part.type === 'tool-input-start') {
      ensure(part.toolCallId, part.toolName)
    } else if (part.type === 'tool-input-delta') {
      const call = byId.get(part.toolCallId)
      if (!call) {
        // Defensive — input-delta without a preceding start. Skip rather than
        // synthesize an unnamed call.
        continue
      }
      call.argsText = `${call.argsText ?? ''}${part.inputTextDelta}`
    } else if (part.type === 'tool-input-available') {
      const call = ensure(part.toolCallId, part.toolName)
      // Keep accumulated argsText if present; otherwise leave undefined.
      // (We don't re-stringify `input` — argsText was the streamed form.)
      call.name = part.toolName ?? call.name
    } else if (part.type === 'tool-output-available') {
      const call = byId.get(part.toolCallId)
      if (!call) {
        continue
      }
      call.result = part.output
      call.state = 'called'
    } else if (part.type === 'tool-output-error') {
      const call = byId.get(part.toolCallId)
      if (!call) {
        continue
      }
      call.result = part.errorText ?? 'tool error'
      call.state = 'errored'
    }
  }

  return order.map(id => {
    const call = byId.get(id)
    // Map entries are guaranteed present because we always set before pushing.
    if (!call) {
      throw new Error(`partsToToolCalls: missing entry for ${id}`)
    }
    return call
  })
}
